import React, { useEffect, useState } from 'react';
import { Col } from 'react-bootstrap';

import { getUser } from '../../modules/contexts/Auth/RequireAuth';
import { ResponsavelAquisicao } from '../../modules/types/Aquisicao';
import {
  headersProaqResponsaveis,
  valoresExibidosProaq,
} from '../../pages/daf/ProcessosAquisitivos/auxiliarProaq';
import {
  handleCreateResponsavel,
  handleDeleteResponsavel,
  handleUpdateResponsavel,
  getAllResponsaveisPorEntidade,
} from '../../services/aquisicoes/responsavelService';
import { isHttpError } from '../../services/httpClient';
import {
  useUsuariosByUnidadeDaf,
  useUsuariosMesmoSetorDaf,
} from '../../services/usuario/userService';
import theme from '../../theme';
import {
  formataNomeMeioSiglas,
  formatDate,
  formatDateSimp,
} from '../../utils/functions';
import {
  swalAttention,
  swalAtualizacao,
  swalDelete,
  swalDeleteConfirm,
  swalErro,
  swalSave,
} from '../../utils/swal';
import BarraInferiorBotoes from '../BarraInferiorBotoes';
import BoxConteudo from '../BoxConteudo';
import CustomButton from '../CustomButton';
import CustomCol from '../CustomCol';
import CustomOffcanvas from '../CustomOffcanvas';
import CustomRow from '../CustomRow';
import CustomSpan from '../CustomSpan';
import Input from '../InputNew';
import Select from '../InputNew/Select';
import Log from '../Log/Log';
import Tabela from '../Tabela';
import WorkspaceBotoes from '../WorkspaceBotoes';
import WorkspaceFicha from '../WorkspaceFicha';
import BolinhaTabelaStatus from '../bolinhaTabelaStatus';

interface Props {
  tab: string;
  id_tab: string;
}
//////OUTROS
const statusOption = [
  { value: 0, label: 'Não informado' },
  { value: 1, label: 'Ativo' },
  { value: 2, label: 'Inativo' },
];

const ResponsavelAquisicaoComponente: React.FC<Props> = ({ tab, id_tab }) => {
  const user = getUser();
  const userID = user.sub;

  //////STATES
  const [show, setShow] = useState(false);
  const [logData, setLogData] = useState<any>();
  const [responsavelId, setResponsavelId] = useState('');

  const usuariosOptions = useUsuariosMesmoSetorDaf();
  const [updating, setUpdating] = useState(false);
  const [statusResponsavelStyle, setStatusResponsavelStyle] =
    useState<React.CSSProperties>({});
  const [statusResponsavel, setStatusResponsavel] = useState(true);
  const [data, setData] = useState<ResponsavelAquisicao[]>();
  const [form, setForm] = useState<ResponsavelAquisicao>({
    id: 0,
    status: '',
    dataInicio: '',
    dataFim: '',
    observacoes: '',
    responsavelId: 0,
    tabId: parseInt(id_tab, 10),
    usuarioId: userID,
  });

  //////FETCH DATA
  const fetchData = async () => {
    try {
      const responsaveis = await getAllResponsaveisPorEntidade(
        tab,
        Number(id_tab),
      );
      setData(responsaveis);
    } catch (error) {
      console.error('Erro ao obter dados', error);
    }
  };

  //////USE EFFECT
  useEffect(() => {
    const verde = '#a5faa5';
    const vermelho = '#ffb4b4';

    const status = Number(form.status);

    const newStatusStyle = {
      backgroundColor: status === 1 ? verde : vermelho,
    };
    setStatusResponsavelStyle(newStatusStyle);

    const deveSerReadonly = status === 1;
    setStatusResponsavel(deveSerReadonly);

    if (deveSerReadonly) {
      setForm((prev) => ({
        ...prev,
        dataFim: '',
      }));
    }
  }, [form.status]);

  useEffect(() => {
    const { dataInicio, dataFim } = form;

    if (dataInicio && dataFim && dataFim < dataInicio) {
      const verificarDatas = async () => {
        await swalAttention(
          `A <b>data final ${formatDateSimp(dataFim)}</b> não pode ser inferior à <b>data de início ${formatDateSimp(dataInicio)}</b>.`,
        );

        setForm((prev) => ({
          ...prev,
          dataFim: '',
        }));
      };

      verificarDatas();
    }
  }, [form.dataInicio, form.dataFim]);

  useEffect(() => {
    fetchData();
  }, []);

  //////TABLE E OFFCANVAS
  const tableData =
    data
      ?.slice() // faz uma cópia para não mutar o array original
      .sort(
        (a, b) =>
          new Date(a.dataInicio).getTime() - new Date(b.dataInicio).getTime(),
      )
      .map((item) => {
        return {
          id: item.id,
          status: (
            <BolinhaTabelaStatus status={item.status == 'S' ? 'S' : 'N'} />
          ),
          dataInicio: formatDateSimp(item.dataInicio),
          dataFim: formatDateSimp(item.dataFim ?? ''),
          nomeResponsavel: item.responsavel?.nomeCompleto || '',
          observacoes: item.observacoes
            ? item.observacoes.substring(0, 60) + '...'
            : '',
        };
      }) || [];

  const offCanvasData =
    data?.map((item) => {
      return {
        id: item.id,
        dataRegistro:
          formatDate(item.dataRegistro ?? '') +
          ' - ' +
          formataNomeMeioSiglas(item.usuarioRegistro?.nomeCompleto ?? ''),
        dataUltimaAtualizacao:
          formatDate(item.dataUltimaAtualizacao ?? '') +
          ' - ' +
          formataNomeMeioSiglas(item.usuarioAtualizacao?.nomeCompleto ?? ''),
        dataUltimaVisualizacao:
          formatDate(item.dataUltimaVisualizacao ?? '') +
          ' - ' +
          formataNomeMeioSiglas(
            item.usuarioUltimaVisualizacao?.nomeCompleto ?? '',
          ),
        nEdicoes: item.nEdicoes,
        nVisualizacoes: item.nVisualizacoes,
        separadorLog: '',
        status:
          item.status === 'S' ? (
            <CustomSpan texto="Ativo" />
          ) : (
            <CustomSpan texto="Inativo" />
          ),
        responsavel: item.responsavel?.nomeCompleto ?? '',
        dataInicio: formatDateSimp(item.dataInicio),
        dataFim: formatDateSimp(item.dataFim ?? ''),
        separador: '',
        observacoes: item.observacoes,
      };
    }) || [];

  //////FUNÇÕES HANDLE
  const handleClearForm = () => {
    setForm({
      id: 0,
      responsavelId: 0,
      dataInicio: '',
      dataFim: '',
      status: '',
      observacoes: '',
      tabId: parseInt(id_tab, 10),
      usuarioId: userID,
    });
    setLogData({
      id: 0,
      usuarioRegistro: '',
      usuarioAtualizacao: '',
      dataRegistro: '',
      dataAtualizacao: '',
      nEdicoes: 0,
    });
  };

  const handleShow = () => setShow((prev) => !prev);

  const handleClickInserirNovo = () => {
    handleClearForm();
    setUpdating(false);
    handleShow();
  };

  const handleRowClickResponsavel = (id: string) => {
    setResponsavelId(id);
    setUpdating(true);
    setShow(true);

    const itemSelecionado = data?.find((d) => d.id == parseInt(id, 10));
    if (itemSelecionado) {
      const formatDateLocal = (iso: string) => iso?.split('T')[0];
      setForm({
        id: itemSelecionado.id,
        responsavelId: Number(itemSelecionado.responsavel?.id ?? '0'),
        dataInicio: itemSelecionado.dataInicio
          ? formatDateLocal(itemSelecionado.dataInicio)
          : '',
        dataFim: itemSelecionado.dataFim
          ? formatDateLocal(itemSelecionado.dataFim)
          : '',
        status: itemSelecionado.status === 'S' ? '1' : '2',
        observacoes: itemSelecionado.observacoes,
        usuarioId: userID,
        tabId: parseInt(id_tab, 10),
      });

      setLogData({
        id: itemSelecionado.id,
        usuarioRegistro: itemSelecionado.usuarioRegistro?.nomeCompleto || '',
        usuarioAtualizacao:
          itemSelecionado.usuarioAtualizacao?.nomeCompleto || '',
        dataRegistro: itemSelecionado.dataRegistro,
        dataAtualizacao: itemSelecionado.dataUltimaAtualizacao,
        nEdicoes: itemSelecionado.nEdicoes,
      });
    }
  };

  //////FUNÇÕES CRUD
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;

    // Campos que devem ser convertidos para número
    const camposNumericos = ['tipoResponsavel', 'responsavelId'];

    setForm((prev: ResponsavelAquisicao) => ({
      ...prev,
      [name]: camposNumericos.includes(name) ? parseInt(value, 10) : value,
    }));
  };

  const handleCreate = async () => {
    try {
      if (!validateForm(form)) throw new Error('Formulário inválido.');
      const formToSend = {
        ...form,
        dataInicio: form.dataInicio,
        dataFim: form.dataFim || null,
        responsavelId: Number(form.responsavelId),
        status: Number(form.status) === 1 ? 'S' : 'N',
      };

      await handleCreateResponsavel(formToSend, tab);
      swalSave();
      fetchData();
      setTimeout(() => handleShow(), 2000);
    } catch (err: any) {
      if (isHttpError(err)) swalErro(err.response?.data.message);
      console.error('Erro ao salvar registro:', err);
    }
  };

  const handleUpdate = async () => {
    try {
      if (!validateForm(form)) throw new Error('Formulário inválido.');
      const formToSend = {
        ...form,
        dataInicio: form.dataInicio,
        dataFim: form.dataFim || null,
        responsavelId: Number(form.responsavelId),
        status: Number(form.status) === 1 ? 'S' : 'N',
      };
      const response = await handleUpdateResponsavel(formToSend);

      swalAtualizacao('Sucesso', response.data.message);
      fetchData();
      setTimeout(() => handleShow(), 2000);
    } catch (err: any) {
      if (isHttpError(err)) swalErro(err.response?.data.message);
      console.error('Erro ao atualizar registro:', err);
    }
  };

  const handleDelete = async () => {
    const result = await swalDelete();
    if (!result) return swalErro('A operação falhou.');
    try {
      await handleDeleteResponsavel(responsavelId);
      swalDeleteConfirm();
      fetchData();
      setTimeout(() => handleShow(), 2000);
    } catch (err: any) {
      if (isHttpError(err)) swalErro(err.response?.data.message);
      console.error('Erro ao excluir registro:', err);
    }
  };

  //////OUTRAS FUNÇÕES
  const validateForm = (form: ResponsavelAquisicao) => {
    const erros = [];
    if (!form.status) erros.push('<b>Status</b> é obrigatório.');
    if (!form.dataInicio) erros.push('<b>Data de Início</b> é obrigatório.');

    if (Number(form.status) === 2) {
      if (!form.dataFim) erros.push('<b>Data de Término</b> é obrigatório.');
    }

    if (Number(form.responsavelId) < 1)
      erros.push('<b>Responsável</b> é obrigatório.');

    if (erros.length > 0) {
      swalAttention(erros.join('<br>'));
      return false;
    }
    return true;
  };

  return (
    <BoxConteudo boxTitulo="Equipe de Apoio" fechado>
      <CustomRow>
        <CustomCol width="100%">
          <Tabela
            headers={headersProaqResponsaveis}
            dataTable={tableData}
            displayValues={valoresExibidosProaq}
            onRowClick={handleRowClickResponsavel}
            dataOffCanvas={offCanvasData}
            botaoAbrirOffCanvas={false}
            showPagination={false}
            mostrarBotaoCopiar={false}
            noOffCanvasTable={true}
          />
        </CustomCol>
      </CustomRow>
      <CustomCol className="d-flex justify-content-end">
        <CustomButton
          label="Novo Registro"
          bgColor={theme.colors.botaoAcao.azul}
          textColor={theme.colors.white}
          onClick={() => {
            setUpdating(false);
            handleClickInserirNovo();
          }}
          disabled={id_tab == 'undefined' ? true : false}
        />
      </CustomCol>

      {/* Modal Responsável /*/}
      <CustomOffcanvas
        title="Técnico de Apoio"
        show={show}
        onClose={handleShow}
        width="60vw"
      >
        <WorkspaceFicha>
          <Log
            {...logData}
            entity="TB_RESP_INST_AQUISICAO"
            id={Number(form.id)}
          />
          <BoxConteudo boxTitulo="Técnico">
            <CustomRow
              style={{ marginTop: '10px', justifyContent: 'flex-start' }}
            >
              <CustomCol width="200px">
                <Select
                  name="status"
                  value={form.status}
                  label="Status"
                  options={statusOption}
                  onChange={handleChange}
                  style={statusResponsavelStyle}
                  required
                />
              </CustomCol>
              <CustomCol width="220px">
                <Input
                  type="date"
                  name="dataInicio"
                  value={form.dataInicio}
                  id="dataInicio"
                  label="Data Início"
                  onChange={handleChange}
                  required
                />
              </CustomCol>
              <CustomCol width="220px">
                <Input
                  type="date"
                  name="dataFim"
                  value={form.dataFim ?? ''}
                  id="dataFim"
                  label="Data Término"
                  onChange={handleChange}
                  required={!statusResponsavel}
                  readonly={statusResponsavel}
                />
              </CustomCol>
            </CustomRow>
            <CustomRow>
              <CustomCol>
                <Select
                  name="responsavelId"
                  value={form.responsavelId}
                  label="Responsável"
                  options={usuariosOptions}
                  onChange={handleChange}
                  required
                />
              </CustomCol>
            </CustomRow>

            <CustomRow style={{ marginTop: '10px' }}>
              <Col>
                <Input
                  id="observacoes"
                  type="textarea"
                  name="observacoes"
                  value={form.observacoes}
                  label="Observações"
                  linhas={5}
                  onChange={handleChange}
                />
              </Col>
            </CustomRow>
          </BoxConteudo>
          <BarraInferiorBotoes>
            {updating ? (
              <WorkspaceBotoes
                delete
                // deleteDisabled={btDisable}
                onClickDelete={handleDelete}
                save
                // saveDisabled={btDisable}
                onClickSave={handleUpdate}
              />
            ) : (
              <WorkspaceBotoes
                inserir
                // inserirDisabled={btDisable}
                onClickInserir={handleCreate}
              />
            )}
          </BarraInferiorBotoes>
        </WorkspaceFicha>
      </CustomOffcanvas>
    </BoxConteudo>
  );
};

export default ResponsavelAquisicaoComponente;
